//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import defaultTheme from './PjpConfigView.scss';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';
import { Flex, Columns, Column, Text, Box } from '@locus-taxy/neo-ui';
import { FormFieldSwitch } from 'common/components/core/form/FormFieldSwitch';
import { FormField } from 'common/components/core/form/FormField';
import { SizeEnum } from 'common/enums/UIEnums';
import isUndefined from 'lodash/isUndefined';
import { EnhancedInput } from 'common/components/core/input';
import StringUtils from 'common/utils/StringUtils';
import classnames from 'classnames';

import { PjpNavigationActionsBar } from 'common/entity/pjpMaster/create/PjpNavigationActionsBar';
import SchedulingConfigRadioGroup from 'common/entity/pjpMaster/create/SchedulingConfigRadioGroup';
import { Loader } from 'common/components/core/loader';
import { ObserverProvider } from 'common/components/core/intersection_observer/ObserverFlag';
import pjpPlansIds from 'common/tests/masterManagement/pjpPlans.json';
import DummyPersonaContainer from 'common/entity/pjpMaster/create/DummyPersonaContainer';
import type { PjPlanConfigurationModelType } from 'common/models/flow/PjPlanConfiguration';
import type { TFunction } from 'react-i18next';
import testIds from 'common/tests/fieldPro/createPlanIds.json';

type Props = {
    theme: Object,
    pjpConfiguration: PjPlanConfigurationModelType,
    onDistributionPlanConfigChange: Function,
    t: TFunction,
    onSchedulingConfigIdChange: Function,
    onToggleNeedsRevenueFairnessAcrossDays: Function,
    onToggleNeedsExistingBeatPlan: boolean,
    isLoading: boolean,
    onNext: Function,
    errorMessage: string,
    onCancel: Function,
    onToggleExtraOutletInFrozenBeats: Function,
    onToggleCanAddDummyBeats: Function,
    deleteDummyPersona: Function,
    onAddDummyPersona: Function,
    editDummyPersona: Function,
    closeDummyPersonaModal: Function,
    openDummyPersonaModal: Function,
    onToggleMultiTrip: (toggeledValue: boolean) => void,
    onPrev: Function,
    enableMultiTrip: boolean,
    noConfigError: boolean,
    schedulingConfigs: Array<{
        description: string,
        id: string,
        label: string
    }>,
    isIncrementalPlan: boolean
};

class PjpConfigView extends React.PureComponent<Props> {
    onPjpConfigCardLoad = ref => {
        this.pjpConfigCard = ref;
    };

    onDistributionOffsetChange = distributionOffset => {
        const { pjpConfiguration, onDistributionPlanConfigChange } = this.props;
        const { distributionPlanConfiguration } = pjpConfiguration;
        onDistributionPlanConfigChange(
            distributionPlanConfiguration.setDistributionOffset(
                distributionOffset
            )
        );
    };

    getActionTitle = () => {
        const { isIncrementalPlan, t } = this.props;
        if (isIncrementalPlan) {
            return (
                <>
                    {t('pjpCreate.incrementalPlan.createIncrementalPlan')}
                    <Text
                        textTransform="none"
                        fontSize={0}
                        color="grey.500"
                        fontWeight="400"
                    >
                        {t('pjpCreate.incrementalPlan.readOnlyConfigNote')}
                    </Text>
                </>
            );
        }
        return t('pjpCreate.createPlan');
    };

    render() {
        const {
            pjpConfiguration,
            theme,
            t,
            onSchedulingConfigIdChange,
            // onTransportModeChange,
            onToggleNeedsRevenueFairnessAcrossDays,
            onToggleNeedsExistingBeatPlan,
            isLoading,
            onNext,
            errorMessage,
            onCancel,
            onToggleExtraOutletInFrozenBeats,
            onToggleCanAddDummyBeats,
            deleteDummyPersona,
            onAddDummyPersona,
            editDummyPersona,
            closeDummyPersonaModal,
            openDummyPersonaModal,
            schedulingConfigs,
            noConfigError,
            onPrev,
            onToggleMultiTrip,
            isIncrementalPlan
        } = this.props;
        const {
            distributionPlanConfiguration,
            needsExistingBeatPlan,
            needsRevenueFairnessAcrossDays,
            schedulingConfigId,
            isExtraOutletAllowedInFrozenBeats,
            createDummies,
            enableMultiTrip
        } = pjpConfiguration;
        const { distributionOffset } = distributionPlanConfiguration;

        return (
            <Columns height="fluid">
                <Column classsName={theme.configContainer}>
                    <Flex
                        flexDirection={Flex.Direction.Column}
                        className={theme.pjpConfigView}
                    >
                        <PjpNavigationActionsBar
                            isNextDisabled={isLoading || noConfigError}
                            noConfigError={noConfigError}
                            title={this.getActionTitle()}
                            onCancel={onCancel}
                            onPrev={onPrev}
                            onNext={onNext}
                            theme={theme}
                            errorMessage={errorMessage}
                        />
                        {isLoading ? (
                            <Loader active theme={theme} />
                        ) : (
                            <Box p={4}>
                                <Columns testId={pjpPlansIds.config.configView}>
                                    <Column
                                        width="fluid"
                                        space="small"
                                        data-test-id={
                                            pjpPlansIds.config.configView
                                        }
                                        className={classnames(
                                            theme.pjpConfigCard
                                        )}
                                        ref={this.onPjpConfigCardLoad}
                                    >
                                        <ObserverProvider
                                            className={theme.pjpConfigCardInner}
                                            rootNode={this.pjpConfigCard}
                                        >
                                            <SchedulingConfigRadioGroup
                                                schedulingConfigs={
                                                    schedulingConfigs
                                                }
                                                disabled={isIncrementalPlan}
                                                selectedConfigId={
                                                    schedulingConfigId.configId
                                                }
                                                onSchedulingConfigIdChange={
                                                    onSchedulingConfigIdChange
                                                }
                                                theme={theme}
                                                t={t}
                                            />
                                            {/*
                                             * commented because of product decision.
                                             * Anyway this portion will be needed in near future (next SalesBeat release).
                                             * so let's not delete the whole thing; let's just comment out.
                                             *
                                             *    <TransportModeRadioGroup
                                             *        selectedBeatId={transportMode}
                                             *        onTransportModeChange={
                                             *            onTransportModeChange
                                             *        }
                                             *        theme={theme}
                                             *        t={t}
                                             *    />
                                             */}
                                            <FormFieldSwitch
                                                disabled={isIncrementalPlan}
                                                theme={theme}
                                                checked={
                                                    needsRevenueFairnessAcrossDays
                                                }
                                                label={t(
                                                    'pjpCreate.pjpConfig.revenueFairnessAcrossDaysNeeded'
                                                )}
                                                onChange={
                                                    onToggleNeedsRevenueFairnessAcrossDays
                                                }
                                            />
                                            <FormFieldSwitch
                                                disabled={isIncrementalPlan}
                                                theme={theme}
                                                checked={enableMultiTrip}
                                                label={t(
                                                    'pjpCreate.pjpConfig.allowMultiTrip'
                                                )}
                                                onChange={onToggleMultiTrip}
                                            />
                                            <FormFieldSwitch
                                                disabled={isIncrementalPlan}
                                                theme={theme}
                                                checked={
                                                    isExtraOutletAllowedInFrozenBeats
                                                }
                                                label={t(
                                                    'pjpCreate.pjpConfig.isExtraOutletAllowedInFrozenBeats'
                                                )}
                                                onChange={
                                                    onToggleExtraOutletInFrozenBeats
                                                }
                                            />
                                            <FormField
                                                disabled={isIncrementalPlan}
                                                theme={theme}
                                                label={t(
                                                    'pjpCreate.pjpConfig.distributionHappensAfter'
                                                )}
                                                error={distributionPlanConfiguration.getValidationMessage(
                                                    'distributionOffset'
                                                )}
                                            >
                                                <EnhancedInput
                                                    disabled={isIncrementalPlan}
                                                    theme={theme}
                                                    type="number"
                                                    className={
                                                        theme.distributionDaysInput
                                                    }
                                                    rightIcon={StringUtils.pluralize(
                                                        'day',
                                                        distributionOffset,
                                                        null,
                                                        false
                                                    )}
                                                    onChange={
                                                        this
                                                            .onDistributionOffsetChange
                                                    }
                                                    value={
                                                        isUndefined(
                                                            distributionOffset
                                                        )
                                                            ? ''
                                                            : distributionOffset
                                                    }
                                                    autoWidth
                                                    columnInput
                                                    inputLabelSize={
                                                        SizeEnum.Small
                                                    }
                                                    required
                                                    showRequired
                                                    data-test-id={
                                                        testIds.config
                                                            .distributionHappensAfter
                                                    }
                                                />
                                            </FormField>
                                            {/* <DistributionBeatConfig
                                                        theme={theme}
                                                        distributionPlanConfiguration={
                                                            distributionPlanConfiguration
                                                        }
                                                        onDistributionPlanConfigChange={
                                                            onDistributionPlanConfigChange
                                                        }
                                                        salesCategories={pjpConfiguration.getSalesCategoryOptions()}
                                                    /> */}
                                            <FormFieldSwitch
                                                disabled={isIncrementalPlan}
                                                theme={theme}
                                                checked={needsExistingBeatPlan}
                                                label={t(
                                                    'pjpCreate.pjpConfig.exisitingBeatPlanNeeded'
                                                )}
                                                onChange={
                                                    onToggleNeedsExistingBeatPlan
                                                }
                                            />
                                        </ObserverProvider>
                                    </Column>
                                    <Column
                                        width="2/5"
                                        space="small"
                                        className={classnames(
                                            theme.pjpConfigCard
                                        )}
                                    >
                                        <ObserverProvider
                                            className={theme.pjpConfigCardInner}
                                            rootNode={this.pjpConfigCard}
                                        >
                                            <FormFieldSwitch
                                                disabled={isIncrementalPlan}
                                                theme={theme}
                                                label={t(
                                                    'pjpCreate.pjpConfig.canAddDummyBeats'
                                                )}
                                                onChange={
                                                    onToggleCanAddDummyBeats
                                                }
                                                checked={createDummies}
                                            />
                                            {createDummies ? (
                                                <DummyPersonaContainer
                                                    disabled={isIncrementalPlan}
                                                    deleteDummyPersona={
                                                        deleteDummyPersona
                                                    }
                                                    editDummyPersona={
                                                        editDummyPersona
                                                    }
                                                    onAddDummyPersona={
                                                        onAddDummyPersona
                                                    }
                                                    t={t}
                                                    closeDummyPersonaModal={
                                                        closeDummyPersonaModal
                                                    }
                                                    openDummyPersonaModal={
                                                        openDummyPersonaModal
                                                    }
                                                    theme={theme}
                                                    pjpConfiguration={
                                                        pjpConfiguration
                                                    }
                                                />
                                            ) : null}
                                        </ObserverProvider>
                                    </Column>
                                </Columns>
                            </Box>
                        )}
                    </Flex>
                </Column>
            </Columns>
        );
    }
}

const ThemedPjpConfigView = compose(
    translate('entityManagement'),
    themr('PjpConfigView', defaultTheme)
)(PjpConfigView);

export default ThemedPjpConfigView;
export {
    PjpConfigView as UndecoratedPjpConfigView,
    ThemedPjpConfigView as PjpConfigView
};
